/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import * as React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { createPortal } from "react-dom"
import { DialRoot } from "dialkit"
import { Settings, X } from "lucide-react"
import "dialkit/styles.css"

import "../styles/layout.css"
import Navigation from "./navigation"
import PageTransition from "./page-transition"
import PresenceCursors from "./PresenceCursors"
import CommandPalette from "./CommandPalette"
import CVRequestModal from "./CVRequestModal"
import InteractiveDotBackground from "./InteractiveDotBackground"
import MotionPreferenceControl from "./MotionPreferenceControl"

const DIALKIT_VISIBILITY_STORAGE_KEY = "dialkit-ui-visible"

function readDialKitVisibility() {
  if (typeof window === "undefined") return false

  try {
    return window.localStorage.getItem(DIALKIT_VISIBILITY_STORAGE_KEY) === "true"
  } catch (_) {
    return false
  }
}

const DialKitCloseButton = ({ onClose }) => {
  const [panelElement, setPanelElement] = React.useState(null)

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined

    let frameId = null
    const findPanel = () => {
      const panel = document.querySelector(".dialkit-panel")
      if (panel) {
        setPanelElement(panel)
        return
      }

      frameId = window.requestAnimationFrame(findPanel)
    }

    findPanel()

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [])

  if (!panelElement) return null

  return createPortal(
    <button
      type="button"
      className="presence-dialkit-close"
      aria-label="Hide DialKit controls"
      title="Hide DialKit controls"
      data-glass-exclude
      onClick={onClose}
    >
      <X size={15} aria-hidden="true" />
    </button>,
    panelElement
  )
}

const Layout = ({ children }) => {
  const [isDialKitVisible, setIsDialKitVisible] = React.useState(readDialKitVisibility)
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  const setDialKitVisibility = React.useCallback(value => {
    setIsDialKitVisible(value)
    try {
      window.localStorage.setItem(DIALKIT_VISIBILITY_STORAGE_KEY, String(value))
    } catch (_) {
      // localStorage can be unavailable in private or restricted contexts.
    }
  }, [])

  return (
    <div className="page-shell flex flex-col min-h-screen" data-glass-root>
      <InteractiveDotBackground />
      <main className="z-1 flex flex-1 font-display overflow-hidden" data-nav-glass-surface>
        <PageTransition>{children}</PageTransition>
      </main>
      <div className="global-quick-controls" data-glass-exclude>
        <CommandPalette />
        <MotionPreferenceControl />
      </div>
      <CVRequestModal />
      <Navigation />
      <PresenceCursors />
      {isDialKitVisible ? (
        <>
          <div className="presence-dialkit" data-glass-exclude>
            <DialRoot position="top-right" productionEnabled />
          </div>
          <DialKitCloseButton onClose={() => setDialKitVisibility(false)} />
        </>
      ) : (
        <button
          type="button"
          className="presence-dialkit-open"
          aria-label="Show DialKit controls"
          title="Show DialKit controls"
          data-glass-exclude
          style={{ top: "1rem", right: "1rem", zIndex: 2147483647 }}
          onClick={() => setDialKitVisibility(true)}
        >
          <Settings size={18} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

export default Layout
